import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag, Space, Button, Tooltip } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import TableIndex from '@shared/table/TableIndex';
import type { TableService, SupportRule } from '@shared/table/TableIndex';
import { formatDateTime } from '@utils/dateTime';
import { MultipleValue } from '@shared/table/Rules/component';
import FullDayRangePicker from '@components/shared/FullDayRangePicker';

interface PartnerApiLogListProps {
  service: TableService;
}

interface PartnerApiLogRow {
  id: string;
  created_at?: string;
  action?: string;
  integration?: { name?: string } | null;
  request_method?: string;
  request_path?: string;
  response_http_status?: number;
  ip_address?: string;
  duration_ms?: number;
  [key: string]: unknown;
}

const PartnerApiLogList: React.FC<PartnerApiLogListProps> = ({ service }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [lastModified, setLastModified] = useState(0);

  const columns = useMemo(() => [
    {
      name: t('common.created_at'),
      selector: (row: PartnerApiLogRow) => formatDateTime(row.created_at as string),
      sortable: true,
      sortFieldName: 'partner_api_logs.created_at',
      width: '180px',
    },
    {
      name: t('models.partner_api_log.action'),
      selector: (row: PartnerApiLogRow) => row.action,
      width: '130px',
      cell: (row: PartnerApiLogRow) => <Tag color="blue">{row.action}</Tag>,
    },
    {
      name: t('models.partner_api_log.integration'),
      selector: (row: PartnerApiLogRow) => row.integration?.name,
      width: '150px',
    },
    {
      name: t('models.partner_api_log.method'),
      selector: (row: PartnerApiLogRow) => row.request_method,
      width: '100px',
      cell: (row: PartnerApiLogRow) => <Tag>{row.request_method}</Tag>,
    },
    {
      name: t('models.partner_api_log.path'),
      selector: (row: PartnerApiLogRow) => row.request_path,
      width: '350px',
      cell: (row: PartnerApiLogRow) => (
        <Tooltip title={row.request_path} placement="topLeft">
          <div style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            cursor: 'default',
            fontSize: 12,
          }}>
            {row.request_path}
          </div>
        </Tooltip>
      ),
    },
    {
      name: t('models.partner_api_log.status'),
      selector: (row: PartnerApiLogRow) => row.response_http_status,
      width: '100px',
      cell: (row: PartnerApiLogRow) => {
        const status = row.response_http_status;
        let color = 'default';
        if (status !== undefined && status >= 200 && status < 300) color = 'success';
        if (status !== undefined && status >= 400 && status < 500) color = 'warning';
        if (status !== undefined && status >= 500) color = 'error';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      name: t('models.partner_api_log.ip_address'),
      selector: (row: PartnerApiLogRow) => row.ip_address,
      width: '130px',
    },
    {
      name: t('models.partner_api_log.duration'),
      selector: (row: PartnerApiLogRow) => `${row.duration_ms?.toFixed(2)} ms`,
      width: '120px',
    },
    {
      name: t('common.table.actions'),
      cell: (row: PartnerApiLogRow) => (
        <Space>
          <Tooltip title={t('common.button.view')}>
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => navigate(`/partner_api_logs/${row.id}`)}
            />
          </Tooltip>
        </Space>
      ),
      width: '80px',
    }
  ], [t, navigate]);

  const supportRules = useMemo<SupportRule[]>(() => [
    {
      label: t('common.created_at'),
      component: FullDayRangePicker,
      inputName: 'partner_api_logs.created_at',
      defaultPinned: true,
    },
    {
      label: t('models.partner_api_log.action'),
      component: MultipleValue,
      inputName: 'partner_api_logs.action',
      createable: true,
    },
    {
      label: t('models.partner_api_log.status'),
      component: MultipleValue,
      inputName: 'partner_api_logs.response_http_status',
      createable: true,
    },
  ], [t]);

  return (
    <TableIndex
      columns={columns}
      searchable={{
        match: 'contains',
        searchFields: [
          t('models.partner_api_log.action'),
          t('models.partner_api_log.path'),
        ],
      }}
      tableId="adminPartnerApiLogsTable"
      defaultQuery={{
        sorting_field: 'partner_api_logs.created_at',
        sorting_direction: 'desc',
      }}
      supportRules={supportRules}
      lastModified={lastModified}
      setLastModified={setLastModified}
      service={service}
    />
  );
};

export default PartnerApiLogList;
